// ===============================================
// File: src/components/Widgets/TaskWidget.jsx
// Description: Quick Task widget (theme-aware)
// - Works in light and dark modes
// - Keeps all existing planner + feedback logic
// ===============================================

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePlanner } from "../../contexts/PlannerContext";
import { useStaff } from "../../contexts/StaffContext";
import { useFeedback } from "../../contexts/FeedbackContext";
import WidgetHeader from "../Common/WidgetHeader";
import { useTheme } from "../../core/theme/ThemeProvider";

/* ---------- Helpers ---------- */
const toISO = (val, fallback = new Date()) => {
  try {
    const d = val ? new Date(val) : new Date(fallback);
    return new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      d.getHours(),
      d.getMinutes(),
      0,
      0
    ).toISOString();
  } catch {
    return new Date(fallback).toISOString();
  }
};

const newId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `pln_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const CATEGORY_HINTS = [
  "Forming",
  "Drying",
  "Bisque",
  "Glazing",
  "Kiln",
  "Packaging",
  "Dispatch",
];

const STAGES = [
  { v: "", label: "—" },
  { v: "forming", label: "Forming" },
  { v: "drying", label: "Drying" },
  { v: "bisque", label: "Bisque" },
  { v: "glazing", label: "Glazing" },
  { v: "glazeFiring", label: "Glaze Firing" },
  { v: "qcPack", label: "QC & Pack" },
  { v: "dispatch", label: "Dispatch" },
];

/* ---------- Component ---------- */
export default function TaskWidget({ backTo = "/dashboard" }) {
  const navigate = useNavigate();
  const { mode } = useTheme();

  // Guards if contexts aren’t mounted yet
  const planner = (() => {
    try {
      return usePlanner();
    } catch {
      return {};
    }
  })();
  const { upsertEvents } = planner;

  const staffCtx = (() => {
    try {
      return useStaff();
    } catch {
      return {};
    }
  })();
  const { staffList = [] } = staffCtx;

  const fb = (() => {
    try {
      return useFeedback();
    } catch {
      return {};
    }
  })();
  const { play, buzz } = fb;

  const nowISO = new Date().toISOString().slice(0, 16);
  const in1hISO = new Date(Date.now() + 60 * 60000).toISOString().slice(0, 16);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [start, setStart] = useState(nowISO);
  const [end, setEnd] = useState(in1hISO);
  const [qty, setQty] = useState(1);
  const [batch, setBatch] = useState("");
  const [stage, setStage] = useState("");
  const [assigneeId, setAssigneeId] = useState("unassigned");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [added, setAdded] = useState(null);

  const canSave = useMemo(() => String(title).trim().length > 0, [title]);
  const plannerReady = typeof upsertEvents === "function";

  /* ---------- Theming ---------- */
  const inputCls =
    "w-full px-2 py-1 rounded text-sm bg-slate-900/40 border border-white/10 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[var(--mudo-primary)] focus:ring-1 focus:ring-[var(--mudo-primary)]";

  const panelCls = "rounded-xl p-4 space-y-3 border border-white/10 bg-slate-800/60";

  const primaryBtn =
    "px-3 py-2 rounded font-semibold transition bg-[var(--mudo-primary)] text-[var(--mudo-onPrimary,#0b0b0b)] hover:brightness-110";
  const secondaryBtn =
    mode === "dark"
      ? "px-3 py-2 rounded border border-white/20 bg-white/5 hover:bg-white/10 text-white"
      : "px-3 py-2 rounded border border-black/15 bg-black/5 hover:bg-black/10 text-black";

  /* ---------- Planner Save ---------- */
  async function addToPlanner() {
    if (!plannerReady || !canSave || saving) return;
    setSaving(true);

    let startISO = toISO(start);
    let endISO = toISO(end || start);
    if (new Date(endISO) <= new Date(startISO)) {
      endISO = toISO(new Date(new Date(startISO).getTime() + 60 * 60000));
    }

    const ev = {
      id: newId(),
      title: title.trim(),
      start: startISO,
      end: endISO,
      qty: Math.max(1, Number(qty) || 1),
      batch: batch.trim() || undefined,
      category: category.trim() || undefined,
      status: "todo",
      progress: 0,
      notes: notes || "",
      tags: stage ? [stage] : [],
      ...(assigneeId === "unassigned"
        ? { assigneeId: null, assignee: null, assigneeName: null }
        : { assigneeId: String(assigneeId) }),
    };

    try {
      await upsertEvents([ev]);
      setAdded(ev);
      play?.("success");
      buzz?.("success");
      document.dispatchEvent(
        new CustomEvent("mudo:planner:updated", { detail: { id: ev.id } })
      );
      setTitle("");
      setBatch("");
      setNotes("");
    } catch (e) {
      play?.("error");
      buzz?.("medium");
      console.warn("Failed to add task:", e);
    } finally {
      setSaving(false);
      setTimeout(() => setAdded(null), 3500);
    }
  }

  const goToPlanner = () => {
    if (!added?.id) return;
    play?.("tap");
    navigate("/planner/tasks", { state: { focusEventId: added.id } });
  };

  const staffLabel = (s) =>
    s?.name ||
    [s?.firstName, s?.lastName].filter(Boolean).join(" ") ||
    String(s?.id || "");

  /* ---------- UI ---------- */
  return (
    <div className="h-full w-full text-white">
      <div className="space-y-3 rounded-lg p-4 h-full flex flex-col bg-slate-900/60 border border-white/10">
        <WidgetHeader
          to={backTo}
          title="Quick Task"
          help="Create a scheduled task quickly. Saving adds it to your Planner tasks so it shows up in your plan."
        />

        {/* success banner */}
        {added && (
          <div className="rounded border border-emerald-500/40 bg-emerald-500/15 text-emerald-300 px-3 py-2 text-sm flex items-center justify-between">
            <div>
              Added: <span className="font-semibold">{added.title}</span>
              <span className="opacity-80">
                {" "}
                — {new Date(added.start).toLocaleString()}
              </span>
            </div>
            <button
              type="button"
              onClick={goToPlanner}
              className="underline font-medium"
              title="Open in Planner"
            >
              View in Planner
            </button>
          </div>
        )}

      {/* form */}
        <div className={panelCls}>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs opacity-80">
            Title
            <input
              className={inputCls}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Task name…"
            />
          </label>

          <label className="text-xs opacity-80">
            Category
            <input
              list="task-category-hints"
              className={inputCls}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Glazing"
            />
            <datalist id="task-category-hints">
              {CATEGORY_HINTS.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs opacity-80">
            Start
            <input
              type="datetime-local"
              className={inputCls}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </label>
          <label className="text-xs opacity-80">
            End
            <input
              type="datetime-local"
              className={inputCls}
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs opacity-80">
            Qty
            <input
              type="number"
              min="1"
              className={inputCls}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </label>

          <label className="text-xs opacity-80">
            Batch
            <input
              className={inputCls}
              value={batch}
              onChange={(e) => setBatch(e.target.value)}
              placeholder="e.g. B24-10"
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs opacity-80">
            Stage
            <select
              className={inputCls}
              value={stage}
              onChange={(e) => setStage(e.target.value)}
            >
              {STAGES.map((s) => (
                <option key={s.v} value={s.v}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs opacity-80">
            Assignee
            <select
              className={inputCls}
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
            >
              <option value="unassigned">Unassigned</option>
              {staffList.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {staffLabel(s)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="text-xs opacity-80 block">
          Notes
          <textarea
            className={`${inputCls} py-2`}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional details…"
          />
        </label>

          <div className="flex justify-end gap-2">
            <button
              className={secondaryBtn}
              onClick={() => {
                setTitle("");
                setBatch("");
                setNotes("");
              }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={addToPlanner}
              disabled={!plannerReady || !canSave || saving}
              className={`${primaryBtn} ${
                plannerReady && canSave && !saving ? "" : "cursor-not-allowed opacity-50"
              }`}
              title={plannerReady ? "" : "Planner not ready"}
            >
              {saving ? "Adding…" : added ? "Added!" : "Add to Planner"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
